import {
  Box,
  Button,
  Collapse,
  DarkTooltip,
  IconButton,
  styled,
  Text,
  Divider,
  Input,
  Stack,
} from '@ltvco/refresh-lib/theme';
import { ArrowUpward, Menu, Search } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { useState, type ReactNode } from 'react';

import { hasValue, type StatusSeverity } from './PreviewCards/utils';

export const FIELD_PLACEHOLDER = '–';

export const CardContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 720,
  backgroundColor: theme.palette.background.paper,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
}));

export const HeaderTitle = styled(Text)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const HeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  color: theme.palette.text.secondary,
  cursor: 'pointer',
}));

export const SearchResultTitle = styled(Text)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  display: 'block',
  marginBottom: 16,
}));

export const SectionDivider = styled('hr')(({ theme }) => ({
  border: 'none',
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: '16px 0',
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 12,
  color: theme.palette.rats.main,
}));

export const SectionTitle = styled(Text)({
  fontWeight: 600,
});

export const OwnerRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 16,
});

export const OwnerInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

export const OwnerAvatar = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  backgroundColor: theme.palette.button.light,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 600,
  flexShrink: 0,
}));

export const OwnerName = styled(Text)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

/**
 * `cursor: text` covers the full row — the ghost span has `pointer-events: none` so
 * its cursor falls through here; clicking anywhere in the row focuses the input.
 * `flexWrap: nowrap` keeps the ghost hint inline; on narrow screens it ellipsizes
 * rather than wrapping onto an unpadded line.
 * `isLanding` switches between full-width column layout and a flex-grow row layout.
 */
export const GhostInputRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLanding',
})<{ isLanding?: boolean }>(({ isLanding }) => ({
  position: 'relative',
  display: 'flex',
  cursor: 'text',
  flexWrap: 'nowrap',
  overflow: 'hidden',
  alignItems: 'flex-start',
  minWidth: 0,
  ...(isLanding ? { width: '100%' } : { flex: 1 }),
}));

/**
 * `minWidth: 0` overrides the flex default of `min-width: auto`, which would otherwise
 * prevent this span from shrinking below its content width and break the ellipsis.
 */
export const GhostText = styled('span')(({ theme }) => ({
  alignSelf: 'flex-start',
  padding: '16px 0',
  color: theme.palette.text.disabled,
  fontSize: '16px',
  lineHeight: 1.5,
  fontStyle: 'italic',
  opacity: 0.6,
  whiteSpace: 'nowrap',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  pointerEvents: 'none',
  userSelect: 'none',
}));

export const GhostMirror = styled('span')({
  position: 'absolute',
  top: 0,
  left: 0,
  height: 0,
  overflow: 'hidden',
  visibility: 'hidden',
  pointerEvents: 'none',
  whiteSpace: 'pre',
  boxSizing: 'border-box',
  padding: '0 0 0 16px',
  fontSize: '16px',
  fontFamily: 'inherit',
  lineHeight: 1.5,
  letterSpacing: 'inherit',
});

export const CardHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const ReportTitle = styled(Text)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 24,
  lineHeight: '32px',
  color: theme.palette.text.primary,
}));

export const CardSectionTitle = styled(Text)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 18,
  lineHeight: '24px',
  color: theme.palette.text.primary,
}));

export const ReportSections = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  marginTop: 12,
  paddingTop: 12,
});

export const ReportSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: '12px 0',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

export const DetailList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const InlineDetailRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
});

export const JustifiedRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  padding: '12px 0 0',
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const RowLabel = styled(Text)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export const Chevron = ({
  open,
  flexShrink,
  hidden,
  transition = 'transform 0.2s',
}: {
  open: boolean;
  flexShrink?: number;
  // When there is nothing to expand (a zero-count row) the chevron is hidden
  // but still occupies its slot, so the count column stays aligned.
  hidden?: boolean;
  transition?: string;
}) => (
  <KeyboardArrowDownIcon
    sx={{
      fontSize: 20,
      color: 'text.disabled',
      transition,
      transform: open ? 'rotate(180deg)' : 'none',
      ...(hidden && { visibility: 'hidden' }),
      ...(flexShrink !== undefined && { flexShrink }),
    }}
  />
);

export const StatusIcon = ({ severity }: { severity: StatusSeverity }) => {
  if (severity === 'success') {
    return <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />;
  }
  if (severity === 'warning') {
    return <WarningRoundedIcon sx={{ fontSize: 20, color: 'warning.main' }} />;
  }
  return <ErrorIcon color="error" sx={{ fontSize: 20 }} />;
};

export const ReportTypeChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignSelf: 'flex-start',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '18px',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.light,
  borderRadius: 4,
  padding: '4px 10px',
  marginBottom: 4,
}));

export const SubsectionTitle = styled('h3')(({ theme }) => ({
  fontSize: 18,
  fontWeight: 700,
  color: theme.palette.text.primary,
  margin: '0 0 16px',
}));

export const FieldList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 11,
});

export const FieldRowItem = styled(Box)({
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap',
  alignItems: 'baseline',
  fontSize: 16,
  lineHeight: 1.4,
  minHeight: 22,
});

export const CompactFieldRowItem = styled(FieldRowItem)({
  fontSize: 14,
  minHeight: 0,
});

export const FieldRowLabel = styled('span')(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

export const FieldRowValue = styled('span', {
  shouldForwardProp: (prop) => prop !== 'missing',
})<{ missing?: boolean }>(({ theme, missing }) => ({
  fontWeight: 700,
  color: missing ? theme.palette.text.disabled : theme.palette.text.primary,
}));

export const VStack = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'gap',
})<{ gap?: number }>(({ gap = 8 }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap,
}));

export const StaticExpandRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  minHeight: 56,
  padding: '8px 0',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': { borderBottom: 'none' },
}));

export const ExpandRow = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  minHeight: 56,
  padding: '8px 0',
  margin: 0,
  background: 'none',
  border: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  fontWeight: 700,
  '&:hover': { backgroundColor: theme.palette.background.default },
  '&:last-of-type': { borderBottom: 'none' },
}));

export const ExpandRowMain = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  gap: 2,
});

export const ExpandRowLabel = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const ExpandRowPreview = styled('span')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.35,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

// Universal zero-count rule: a count of 0 dims to text.disabled; any non-zero
// count uses text.primary. Keeping this in one place means every card
// (Phone/Email/Person/Ancestry/Username/…) stays consistent.
export const ExpandRowCount = ({ count }: { count: number }) => (
  <Box
    component="span"
    sx={{
      fontSize: 16,
      fontWeight: 700,
      color: count > 0 ? 'text.primary' : 'text.disabled',
      flexShrink: 0,
    }}
  >
    {count}
  </Box>
);

export const ExpandPanelInner = styled(Box)({ padding: '12px 0 16px' });

export const AccordionCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
}));

export const AccordionCardButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'boldText',
})<{ boldText?: boolean }>(({ theme, boldText = true }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  minHeight: 44,
  padding: '12px 16px',
  background: 'none',
  border: 'none',
  borderBottom: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  ...(boldText && { fontWeight: 700 }),
  '&[aria-expanded="true"]': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

export const ExpandSection = ({
  label,
  preview,
  count,
  children,
}: {
  label: string;
  preview?: string;
  count?: number;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>{label}</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        {count !== undefined && count !== null && (
          <ExpandRowCount count={count} />
        )}
        <Chevron open={open} hidden={count === 0} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>{children}</ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

export const OwnerAccordionRow = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  minHeight: 56,
  padding: '8px 0',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  fontWeight: 700,
});

export const OwnerAccordionMain = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  gap: 4,
});

export const ItemTextStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  gap: 4,
});

export const OwnerAccordionName = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.3,
  color: theme.palette.text.primary,
}));

export const OwnerAccordionRole = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.35,
  color: theme.palette.text.secondary,
}));

export const RecordItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'surface',
})<{ surface?: 'default' | 'paper'; type?: string }>(({ theme }) => ({
  padding: '10px 12px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const ContactRecordList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
});

export const ContactRecordRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

export const ContactRecordTextStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
});

export const ContactRecordTitle = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.4,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ContactRecordSub = styled('span')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  marginTop: 1,
}));

export const RecordItemTitle = styled(Box)({
  fontSize: 16,
  fontWeight: 700,
});

export const RecordItemSub = styled(Box)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  marginTop: 4,
}));

interface ContactRecordItemProps {
  icon: ReactNode;
  title: string;
  sub?: string;
  onClick?: () => void;
}

export const ContactRecordItem = ({
  icon,
  title,
  sub,
  onClick,
}: ContactRecordItemProps) => (
  <RecordItem
    component={onClick ? 'button' : 'div'}
    type={onClick ? 'button' : undefined}
    surface="paper"
    onClick={onClick}
    sx={
      onClick
        ? {
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            background: 'none',
            border: 'none',
            '&:hover': { backgroundColor: 'action.hover' },
          }
        : undefined
    }
  >
    <ContactRecordRow>
      {icon}
      <ContactRecordTextStack>
        <ContactRecordTitle>{title}</ContactRecordTitle>
        {sub ? <ContactRecordSub>{sub}</ContactRecordSub> : null}
      </ContactRecordTextStack>
    </ContactRecordRow>
  </RecordItem>
);

export const StatusFieldRowItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  minHeight: 28,
  flexWrap: 'nowrap',
  fontSize: 16,
  lineHeight: 1.3,
});

export const StatusFieldContent = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  flexWrap: 'nowrap',
});

export const StatusFieldText = styled('span')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  // Label tier matches FieldRowLabel (text.secondary/400); the nested
  // StatusFieldValue carries its own text.primary/700 for the value.
  color: theme.palette.text.secondary,
}));

export const StatusFieldValue = styled('span')(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const AccordionDetailList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const OwnerAccordionPanel = styled(Box)({
  padding: '2px 0 12px',
});

export const OwnerCard = styled(Box)<{ type?: string }>(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
}));

export const OwnerCardHeader = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  minHeight: 56,
  padding: '12px 16px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 700,
  textAlign: 'left',
  '&:disabled': { cursor: 'default' },
});

export const OwnerCardName = styled('span')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const OwnerCardContent = styled(Box)({
  padding: '0 16px 16px',
});

export const OwnerReportBar = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  backgroundColor: theme.palette.background.default,
  borderRadius: 8,
  padding: '12px 14px',
  marginBottom: 14,
  width: '100%',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const OwnerReportText = styled('span')(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.primary,
  lineHeight: 1.45,
}));

export const OwnerReportButton = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

export const StatusFieldIcon = ({
  severity,
}: {
  // 'neutral' extends the shared StatusSeverity locally for non-alarming
  // states (e.g. an expired-but-fine warranty) — kept off the shared union
  // so StatusIcon/StatusConfig/StatusFieldRow aren't forced to handle it.
  severity: StatusSeverity | 'neutral';
}) => {
  if (severity === 'success') {
    return (
      <CheckCircleIcon color="success" sx={{ fontSize: 16, flexShrink: 0 }} />
    );
  }
  if (severity === 'warning') {
    return (
      <WarningRoundedIcon
        sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }}
      />
    );
  }
  if (severity === 'neutral') {
    return (
      <RemoveCircleIcon
        sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
      />
    );
  }
  return <ErrorIcon color="error" sx={{ fontSize: 16, flexShrink: 0 }} />;
};

const FieldInfoTip = ({ text, label }: { text: string; label?: string }) => (
  <DarkTooltip title={text} placement="top">
    <IconButton
      size="small"
      aria-label={label ?? 'More information'}
      sx={{
        padding: 0,
        width: 20,
        height: 20,
        color: 'text.disabled',
        flexShrink: 0,
      }}
    >
      <InfoIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </DarkTooltip>
);

interface PreviewCardSectionProps {
  title: string;
  children: ReactNode;
}

export const PreviewCardSection = ({
  title,
  children,
}: PreviewCardSectionProps) => (
  <>
    <SectionDivider />
    <SubsectionTitle>{title}</SubsectionTitle>
    {children}
  </>
);

interface ReportFieldRowProps {
  label: string;
  value?: string | null;
}

export const ReportFieldRow = ({ label, value }: ReportFieldRowProps) => {
  const missing =
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'string' && !hasValue(value));

  return (
    <FieldRowItem>
      <FieldRowLabel>{label}:</FieldRowLabel>
      <FieldRowValue missing={missing}>
        {missing ? FIELD_PLACEHOLDER : value}
      </FieldRowValue>
    </FieldRowItem>
  );
};

interface CompactReportFieldRowProps {
  label: string;
  value?: string | null;
}

export const CompactReportFieldRow = ({
  label,
  value,
}: CompactReportFieldRowProps) => {
  const missing =
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'string' && !hasValue(value));

  return (
    <CompactFieldRowItem>
      <FieldRowLabel>{label}:</FieldRowLabel>
      <FieldRowValue missing={missing}>
        {missing ? FIELD_PLACEHOLDER : value}
      </FieldRowValue>
    </CompactFieldRowItem>
  );
};

interface StatusFieldRowProps {
  label: string;
  value: string;
  severity: StatusSeverity;
  info?: string;
  infoLabel?: string;
}

export const StatusFieldRow = ({
  label,
  value,
  severity,
  info,
  infoLabel,
}: StatusFieldRowProps) => (
  <StatusFieldRowItem>
    <StatusFieldIcon severity={severity} />
    <StatusFieldContent>
      <StatusFieldText>
        {label}: <StatusFieldValue>{value}</StatusFieldValue>
      </StatusFieldText>
      {info ? <FieldInfoTip text={info} label={infoLabel} /> : null}
    </StatusFieldContent>
  </StatusFieldRowItem>
);

interface ViewReportButtonProps {
  onClick: () => void;
}

export const ViewReportButton = ({ onClick }: ViewReportButtonProps) => (
  <IconButton
    onClick={onClick}
    size="small"
    aria-label="View full report"
    sx={{
      ml: 'auto',
      color: 'text.secondary',
      '&:hover': { color: 'text.primary' },
    }}
    disableRipple
  >
    <OpenInFullIcon sx={{ fontSize: 20 }} />
  </IconButton>
);

export const ShowMoreRelativesToggle = ({
  expanded,
  hiddenCount,
  onToggle,
}: {
  expanded: boolean;
  hiddenCount: number;
  onToggle: () => void;
}) => (
  <Box
    component="button"
    onClick={onToggle}
    sx={{
      mt: 1,
      background: 'none',
      border: 'none',
      padding: '4px 0',
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: 14,
      fontWeight: 700,
      color: 'primary.main',
    }}
  >
    {expanded ? 'Show less' : `Show ${hiddenCount} more`}
  </Box>
);

export const SeeFullReportCTA = ({ onClick }: ViewReportButtonProps) => (
  <Button
    variant="outlined"
    size="small"
    fullWidth
    onClick={onClick}
    sx={{ mt: 2 }}
  >
    See Full Report
  </Button>
);

// Unified stat card (per #4) — replaces Vehicle MetricCard, Property
// ValuationCard, and Property TaxCard.
export const MetricsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: 10,
});

const MetricCardRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 14,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
}));

const MetricCardIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  color: theme.palette.primary.main,
  '& .MuiSvgIcon-root': { fontSize: 24 },
}));

const MetricCardLabel = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.3,
  color: theme.palette.text.secondary,
}));

const MetricCardValue = styled('span')(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1.15,
  color: theme.palette.text.primary,
  fontVariantNumeric: 'tabular-nums',
}));

// Box (not span) so multiple sub lines stack (Property valuation shows
// "Last updated …" plus a source line).
const MetricCardSub = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.3,
  color: theme.palette.text.secondary,
}));

export interface MetricCardProps {
  label: string;
  value: string;
  sub?: ReactNode;
  icon?: ReactNode;
}

export const MetricCard = ({ label, value, sub, icon }: MetricCardProps) => (
  <MetricCardRoot>
    {icon ? <MetricCardIcon>{icon}</MetricCardIcon> : null}
    <MetricCardLabel>{label}</MetricCardLabel>
    <MetricCardValue>{value}</MetricCardValue>
    {sub ? <MetricCardSub>{sub}</MetricCardSub> : null}
  </MetricCardRoot>
);

export const OVERLAY_HEADER_HEIGHT = 49; // padding(12) + body1 line-height(24) + padding(12) + border(1)

export const OverlayHeader = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 20,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const OverlayContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 10,
  overflow: 'auto',
  scrollPaddingTop: OVERLAY_HEADER_HEIGHT,
  backgroundColor: theme.palette.background.paper,
  animation: 'overlayFadeIn 0.25s ease-out',
  '@keyframes overlayFadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  '@media (max-width: 1024px)': {
    position: 'fixed',
    zIndex: 1300,
  },
}));

export const OverlayContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  minHeight: '100%',
  maxWidth: 1120,
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '100%',
}));

export const ReportWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  '& #report-column-container': {
    width: '100% !important',
    maxWidth: '800px !important',
    minWidth: '0 !important',
    height: 'auto !important',
    overflowY: 'unset !important',
    margin: '0 auto',
    animation: 'none !important',
  },
  '& #report-column-header': {
    display: 'none',
  },
  '& #person-overview, & #email-overview, & #vehicle-overview, & #property-overview, & #username-overview':
    {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
}));

// ---- relocated from shared/chat ----
// Interfaces
interface SidebarProps {
  isHovered?: boolean;
  isAnimatingOut?: boolean;
}
interface LandingPageContainerProps {
  landingOpacity: number;
  isTransitioning: boolean;
}

interface ChatPageInputContainerProps {
  isLanding?: boolean;
}

interface AnimatedInputContainerProps {
  isLanding?: boolean;
  isAnimating?: boolean;
  animationPhase?: 'center' | 'moving-up' | 'moving-left' | 'header';
  termsPosition?: { top: number; left: number; width: number };
  showSuggestions?: boolean;
}

interface AnimatedButtonContainerProps {
  isReportOpen?: boolean;
  isLanding?: boolean;
  isAnimating?: boolean;
}

interface AnimatedSendButtonProps {
  isAnimating?: boolean;
}

interface ButtonProps {
  hasInputValue: boolean;
  isLanding?: boolean;
}

//Landing Section Components

export const ChatBvLogoButton = styled(Button)({
  padding: '0 16px 0 16px',
  '&.MuiButtonBase-root:hover': {
    backgroundColor: 'unset !important',
  },
});

export const LandingPageContainer = styled(Box)<LandingPageContainerProps>(
  ({ theme, landingOpacity, isTransitioning }) => ({
    padding: '30px 15px',
    height: '95%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    opacity: landingOpacity,
    transition: 'opacity 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: isTransitioning ? 1 : 2,
    pointerEvents: landingOpacity === 0 ? 'none' : 'auto',
    '@media (min-width: 1024px)': {
      height: '100%',
    },
    [theme.breakpoints.up('md')]: {
      padding: '30px',
    },
  })
);

export const StyledInput = styled(Input)(() => ({
  border: 'unset',
  width: '100%',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'unset',
  },
}));

export const SendButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'hasInputValue',
})<ButtonProps>(({ theme, hasInputValue }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: hasInputValue ? theme.palette.primary.main : 'transparent',
  color: hasInputValue
    ? theme.palette.text.contrastText
    : theme.palette.text.secondary,
  border: 'unset',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  padding: '0',
  minWidth: 'unset',
  transition: 'background-color 0.2s ease',
  marginRight: '10px !important',
  marginBottom: '7px',
  '&:hover': {
    backgroundColor: hasInputValue ? theme.palette.primary.dark : 'transparent',
  },
}));

export const ArrowUpwardIcon = styled(ArrowUpward, {
  shouldForwardProp: (prop) => prop !== 'hasInputValue',
})<ButtonProps>(({ theme, hasInputValue }) => ({
  width: '20px',
  height: '20px',
  color: hasInputValue
    ? theme.palette.text.contrastText
    : theme.palette.text.secondary,
  transition: 'color 0.2s ease',
}));

export const NewChatButton = styled(Button)(() => ({
  width: '32px',
  height: '32px',
  minWidth: '32px',
  borderRadius: '50%',
}));

export const ChatChipsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'lightBlueHover',
})<{ lightBlueHover?: boolean }>(({ theme, lightBlueHover }) => ({
  height: '38px',
  width: 'auto',
  minWidth: '120px',
  borderRadius: '20px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  padding: '0 12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: lightBlueHover
      ? theme.palette.primary.light
      : theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
    // Light-blue variant: tint the label too (cascades to the Typography).
    ...(lightBlueHover && { color: theme.palette.primary.main }),
  },
}));

export const ChatChipsCircleIcon = styled(Box)(({ theme }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const LandingTextSectionStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    fontSize: '36px',
  },
}));

// Chat Page Components
export const ChatPageContainer = styled(Box)(() => ({
  height: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-around',
  position: 'relative',
  '@media (min-width: 1025px)': {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
}));

export const ChatPageBoxContainer = styled(Box)<SidebarProps>(
  ({ theme, isAnimatingOut }) => ({
    backgroundColor: theme.palette.background.default,
    animation: isAnimatingOut
      ? 'slideOut 0.3s ease-in'
      : 'slideIn 0.3s ease-out',
    '@keyframes slideIn': {
      '0%': { transform: 'translateX(-100%)' },
      '100%': { transform: 'translateX(0)' },
    },
    '@keyframes slideOut': {
      '0%': { transform: 'translateX(0)' },
      '100%': { transform: 'translateX(-100%)' },
    },
  })
);

export const ChatPageContainerStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  padding: '0 10px 10px',
  height: '98%',
  margin: '30px 0',
  [theme.breakpoints.up('md')]: {
    padding: '42px 10px 10px',
  },
  '@media (min-width: 1024px)': {
    margin: '8px',
  },
}));

export const MessagesContentStack = styled(Stack)(() => ({
  overflowY: 'auto',
  minHeight: '0',
  flex: 1,
  width: '99%',
}));

export const ChatPageMessagesContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  flexGrow: 1,
  [theme.breakpoints.up('md')]: {
    flexGrow: 1,
  },
}));

export const MessageBoxContainer = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  width: '100%',
  marginBottom: '10px',
  maxWidth: '100%',
  margin: '0px',
}));

// Chat Page Sidebar Components
export const ChatPageSidebarContainer = styled(Box)({
  height: '100%',
  zIndex: 1,
  top: 0,
});

export const SidebarIcon = styled(Box)(({ theme }) => ({
  width: '20px',
  height: '20px',
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

export const SidebarText = styled(Text)<SidebarProps>(({ isHovered }) => ({
  opacity: isHovered ? 1 : 0,
  transform: isHovered ? 'translateX(0)' : 'translateX(-10px)',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  transitionDelay: '0.1s',
  whiteSpace: 'nowrap',
  pointerEvents: isHovered ? 'auto' : 'none',
  visibility: isHovered ? 'visible' : 'hidden',
  width: isHovered ? 'auto' : '0px',
  overflow: 'hidden',
}));

export const SidebarStack = styled(Stack)<SidebarProps>(({ isHovered }) => ({
  width: '100%',
  paddingLeft: isHovered ? '20px' : '0px',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
}));

// Chat Page Input Components

export const ChatPageInputContainer = styled(Box)<ChatPageInputContainerProps>(
  ({ theme, isLanding }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: '10px 10px 0',
    borderRadius: '10px',
    width: '100%',
    marginBottom: '0',
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    '@media (min-width: 1024px)': {
      padding: '10px 0',
      marginBottom: 'clamp(40px, 8vh, 120px)', // Responsive margin based on viewport height
      position: 'relative',
      zIndex: 0,
      maxWidth: '740px',
      width: '98%',
    },
    ...(isLanding && {
      width: '100%',
      padding: '10px 0 0',
      maxWidth: '100%',
      margin: '0 auto 32px auto',
      position: 'relative',
      transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      [theme.breakpoints.up('md')]: {
        width: '60%',
      },
    }),
  })
);

export const AnimatedInputContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    !['isLanding', 'isAnimating', 'animationPhase', 'termsPosition'].includes(
      prop as string
    ),
})<AnimatedInputContainerProps>(
  ({ theme, isLanding, isAnimating, animationPhase, termsPosition }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: '10px 10px 0',
    borderRadius: '10px',
    width: '100%',
    marginBottom: '0',
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    ...(isLanding && {
      position: 'relative',
    }),
    '@media (min-width: 1024px)': {
      padding: '10px 0 0',
      marginBottom: 'clamp(40px, 6vh, 120px)',
      position: 'relative',
      zIndex: 0,
      maxWidth: '740px',
      width: '98%',
    },
    [theme.breakpoints.up('md')]: {
      padding: '4px 10px 10px',
      margin: '0 !important',
    },
    ...(isLanding && {
      width: '100%',
      padding: '10px 0 0',
      maxWidth: '100%',
      margin: '0 auto 32px auto',
      position: 'relative',
      transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      [theme.breakpoints.up('md')]: {
        width: '60%',
      },

      ...(isAnimating && {
        zIndex: 20,
        transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        // Moving the input down according to logo animation
        ...(animationPhase === 'moving-up' && {
          transform: `translateY(${termsPosition?.top || 0}px)`,
          position: 'relative',
        }),
        ...(animationPhase === 'moving-left' && {
          transform: `translateY(${termsPosition?.top || 0}px)`,
          position: 'relative',
        }),
        // Completing the input animation according to logo animation
        ...(animationPhase === 'header' && {
          transform: `translateY(${termsPosition?.top || 0}px)`,
          position: 'relative',
        }),
      }),
    }),
  })
);

export const AnimatedInputButtonContainer = styled(Stack, {
  shouldForwardProp: (prop) =>
    !['isLanding', 'isAnimating', 'animationPhase', 'showSuggestions'].includes(
      prop as string
    ),
})<AnimatedInputContainerProps>(
  ({ theme, isLanding, isAnimating, animationPhase, showSuggestions }) => ({
    flexDirection: isLanding ? 'column' : 'row',
    border: 'none',
    borderRadius: showSuggestions ? '16px 16px 0 0' : '16px',
    position: 'relative',
    background:
      'linear-gradient(271.66deg, rgba(20, 138, 230, 0.86) 1.32%, rgba(81, 201, 245, 0.86) 5.5%, rgba(0, 82, 255, 0.86) 53.66%, rgba(146, 216, 73, 0.7998) 113.22%)',
    transition: 'border-radius 0.15s ease-out',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '3px',
      left: '3px',
      right: '3px',
      bottom: showSuggestions ? '0px' : '3px',
      background: `${theme.palette.background.paper}`,
      borderRadius: showSuggestions ? '13px 13px 0 0' : '13px',
      zIndex: 1,
      transition: 'bottom 0.15s ease-out, border-radius 0.15s ease-out',
    },
    '& > *': {
      position: 'relative',
      zIndex: 2,
    },
    alignItems: 'flex-end',
    height: isLanding ? 'auto' : 'auto',
    minHeight: isLanding ? 'auto' : '56px',
    padding: isLanding ? '13px' : '3px',
    width: isLanding ? '100%' : 'auto',
    marginBottom: 0,
    boxShadow: isLanding
      ? `0px 2px 15px 0px ${theme.palette.divider}`
      : `0px 4px 8px -2px ${theme.palette.divider}`,

    // Styles for landing animation
    ...(isLanding && {
      // The catch-all `all 2s` is only needed while the logo animation runs (it drives
      // the height/padding/border-radius phase changes). At rest it also caught the
      // border-radius change that connects the pill to the suggestions dropdown, making
      // the bottom corners square off over 2s while the inner background + dropdown snap
      // in 0.15s — the "white space on the borders that slowly fills". Keep the connect
      // fast at rest and only use `all 2s` during the animation.
      transition: isAnimating
        ? 'all 2s cubic-bezier(0.4, 0, 0.2, 1)'
        : 'border-radius 0.15s ease-out',
      boxShadow: '0px 2px 15px 0px rgba(0,0,0,0.1)',
      overflow: 'hidden',

      ...(isAnimating &&
        animationPhase === 'moving-up' && {
          maxHeight: '70px',
          height: '70px',
          padding: '8px 16px',
          flexDirection: 'row',
          alignItems: 'center',
          boxShadow: '0px 3px 10px 0px rgba(0,0,0,0.1)',
          borderRadius: '12px',
        }),
      ...(isAnimating &&
        animationPhase === 'moving-left' && {
          maxHeight: '60px',
          height: '60px',
          padding: '4px 16px',
          flexDirection: 'row',
          alignItems: 'center',
          boxShadow: '0px 3px 8px -1px rgba(0,0,0,0.1)',
          borderRadius: '14px',
        }),
      ...(isAnimating &&
        animationPhase === 'header' && {
          height: '56px',
          padding: '0',
          flexDirection: 'row',
          alignItems: 'center',
          borderRadius: '16px',
          width: '97%',
          border: '1px solid var(--mui-palette-divider)',
          boxShadow: '0px 4px 8px -2px rgba(0,0,0,0.1)',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          '@media (min-width: 1024px)': {
            position: 'relative',
            zIndex: 0,
            maxWidth: '740px',
          },
        }),
    }),
  })
);

export const StyledInputChat = styled('textarea')(() => ({
  border: 'unset',
  width: '97%',
  borderRadius: '16px',
  resize: 'none',
  outline: 'none',
  fontFamily: 'inherit',
  fontSize: '16px',
  lineHeight: '1.5',
  padding: '16px',
  backgroundColor: 'transparent',
  color: 'inherit',
  minHeight: '24px',
  maxHeight: '120px',
  overflowX: 'hidden',
  overflowY: 'auto',
  '&::placeholder': {
    color: 'inherit',
    opacity: 0.6,
  },
}));

export const ExpandedPlaceholderContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  minHeight: '56px',
  padding: '0 14px',
  borderRadius: '16px',
  backgroundColor: 'transparent',
}));

export const ExpandedPlaceholderText = styled(Text)<{ textWidth: number }>(
  ({ theme, textWidth }) => ({
    position: 'absolute',
    left: `${14 + textWidth + 8}px`,
    top: '19px',
    color: theme.palette.text.disabled,
    fontSize: '16px',
    opacity: 0.6,
    fontStyle: 'italic',
    pointerEvents: 'none',
    zIndex: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: `calc(100% - ${14 + textWidth + 8 + 14}px)`,
    right: '14px',
  })
);

export const StyledInputChatWithExpandedPlaceholder = styled('textarea')(
  () => ({
    border: 'unset',
    width: '100%',
    borderRadius: '16px',
    backgroundColor: 'transparent',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: '16px',
    lineHeight: '1.5',
    padding: '16px',
    color: 'inherit',
    minHeight: '24px',
    maxHeight: '120px',
    overflowX: 'hidden',
    overflowY: 'auto',
    position: 'relative',
    zIndex: 3,
    '&::placeholder': {
      color: 'inherit',
      opacity: 0.6,
    },
  })
);
export const AnimatedButtonContainer = styled(
  Stack
)<AnimatedButtonContainerProps>(({ isReportOpen, isLanding, isAnimating }) => ({
  flexDirection: 'row',
  gap: '8px',
  width: isReportOpen ? '0' : '12%',
  marginTop: isLanding && !isAnimating ? '36px' : '0',
  justifyContent: 'end',
  alignItems: 'center',
  display: 'flex',
  padding: isLanding && !isAnimating ? '10px' : '0',
  borderRadius: isLanding && !isAnimating ? '16px' : '0',
  opacity: 1,
  transition:
    'opacity 0.3s ease-in-out, padding 0.3s ease-in-out, margin-top 0.3s ease-in-out',
  minWidth: '40px',
  height: '100%',
  ...(isAnimating && {
    alignItems: 'center',
    justifyContent: 'end',
    padding: '0 4px',
    minWidth: '48px',
    height: '100%',
    flexShrink: 0,
    marginTop: '0',
  }),
}));

export const AnimatedSendButton = styled('div')<AnimatedSendButtonProps>(
  ({ isAnimating }) => ({
    opacity: 1,
    visibility: 'visible',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isAnimating && {
      position: 'relative',
      margin: '0',
      width: '32px',
      height: '32px',
      flexShrink: 0,
    }),
  })
);

// Chat Page Report Column Components

export const ReportColumnContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  margin: '0',
  overflowY: 'auto',
  paddingTop: 0,
  '@media (min-width: 1024px)': {
    margin: '8px',
    marginLeft: '0',
  },
}));

export const PersonIconContainer = styled(Stack)(({ theme }) => ({
  borderRadius: '50%',
  backgroundColor: theme.palette.background.default,
  padding: '10px 0',
  width: '80px',
  height: '80px',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    padding: '10px',
  },
}));

export const ResizeButton = styled(Button)(() => ({
  top: '10px',
  left: '10px',
  zIndex: 1000,
  minWidth: 0,
  padding: 0,
  margin: 0,
  width: '30px',
  position: 'unset',
  height: '30px',
}));

// Chat Page Divider Components

export const AdjustedDivider = styled(Divider)(({ theme }) => ({
  width: '100%',
  margin: '20px 0 !important',
  border: `1px solid ${theme.palette.divider}`,
}));

export const MessageContainerDivider = styled(Divider)(({ theme }) => ({
  width: '100%',
  marginBottom: '10px',
  marginTop: '10px',
  border: `1px solid ${theme.palette.divider}`,
}));

// Chat Page Menu Components
export const ChatMenuStack = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: '10px 10px 10px 0',
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'sticky',
  top: '0',
  zIndex: '100',
  flexShrink: 0,
}));

export const MenuIcon = styled(Menu)(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

// Autosuggestion Components
export const AutosuggestionContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  backgroundColor: 'transparent',
  borderRadius: '0 0 16px 16px',
  background:
    'linear-gradient(271.66deg, rgba(20, 138, 230, 0.86) 1.32%, rgba(81, 201, 245, 0.86) 5.5%, rgba(0, 82, 255, 0.86) 53.66%, rgba(146, 216, 73, 0.7998) 113.22%)',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  zIndex: 1000,
  marginTop: '-3px',
  padding: '3px',
  opacity: 0,
  visibility: 'hidden',
  transform: 'translateY(-10px)',
  transition:
    'opacity 0.15s ease-out, visibility 0.15s ease-out, transform 0.15s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0px',
    left: '3px',
    right: '3px',
    bottom: '3px',
    background: `${theme.palette.background.paper}`,
    borderRadius: '0 0 13px 13px',
    zIndex: -1,
  },
  '&.visible': {
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(0)',
  },
}));

export const AutosuggestionTitle = styled(Text)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '12px',
  display: 'block',
  padding: '16px 16px 0 16px',
}));

export const AutosuggestionList = styled(Stack)(() => ({
  flexDirection: 'column',
  gap: '8px',
  padding: '0 16px 16px 16px',
}));

export const AutosuggestionItem = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  textTransform: 'none',
  // Left-align wrapped text (MUI Button centers by default).
  textAlign: 'left',
  justifyContent: 'flex-start',
  fontSize: '14px',
  fontWeight: 400,
  minHeight: '48px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const AutosuggestionIcon = styled(Search)(({ theme }) => ({
  width: '16px',
  height: '16px',
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

//Click here tooltip
export const ClickHereTooltipContainer = styled(Box)(({ theme }) => ({
  // Mobile: fixed + centered in the viewport (not anchored to the inline trigger).
  position: 'fixed',
  bottom: '96px',
  left: '50%',
  right: 'auto',
  transform: 'translateX(-50%)',
  width: 'calc(100vw - 32px)',
  maxWidth: '340px',
  margin: 0,
  backgroundColor: 'rgba(61, 61, 61, 0.9)',
  color: 'white',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 1000,

  // Tablet/desktop: anchor above the inline trigger (original behavior).
  [theme.breakpoints.up('sm')]: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    right: 'auto',
    transform: 'translateX(-50%)',
    width: '400px',
    maxWidth: 'none',
    margin: '0 0 8px',
  },

  [theme.breakpoints.up('md')]: {
    width: '678px',
  },
}));

export const ClickHereTooltipArrow = styled(Box)(({ theme }) => ({
  // Hidden on mobile — the popover is centered there, not anchored to the trigger.
  display: 'none',
  position: 'absolute',
  top: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '6px solid transparent',
  borderRight: '6px solid transparent',
  borderTop: '6px solid rgba(61, 61, 61, 0.9)',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
}));
